/**
 * How the account deletion/deactivation request failed, from the response body (never NSwag's generic
 * exception message):
 * - `retryable`: `503 deletion-refused`, nothing was deleted and a retry is safe;
 * - `last-active-administrator`, `deletion-in-progress`, `other-operation-pending`: the definite `409`
 *   refusals of the M1c lifecycle guard (#3866). Nothing was changed by the request;
 * - `unknown`: anything else, including every other 503 and a timeout. The outcome is not known.
 */
export type AccountDeletionError =
  | 'retryable'
  | 'last-active-administrator'
  | 'deletion-in-progress'
  | 'other-operation-pending'
  | 'unknown';

/** The API's 409 codes for a refused deletion, each decided before anything is reserved or destroyed. */
const CONFLICTS: ReadonlyMap<string, AccountDeletionError> = new Map([
  ['last-active-administrator', 'last-active-administrator'],
  ['deletion-in-progress', 'deletion-in-progress'],
  ['target-deactivation-pending', 'other-operation-pending'],
]);

const SERVICE_UNAVAILABLE_CODES = ['deletion-refused', 'deletion-interrupted'];

/** Classify the response body, not NSwag's generic exception message. */
export function classifyAccountDeletionError(
  error: unknown
): AccountDeletionError {
  if (!error || typeof error !== 'object' || !('status' in error)) {
    return 'unknown';
  }

  if (error.status === 409) {
    const code = readStructuredCode(error, [...CONFLICTS.keys()]);
    return (code && CONFLICTS.get(code)) || 'unknown';
  }

  if (error.status !== 503) {
    return 'unknown';
  }

  const code = readStructuredCode(error, SERVICE_UNAVAILABLE_CODES);
  return code === 'deletion-refused' ? 'retryable' : 'unknown';
}

/**
 * The response's `code`, if it is one of `known`: the structured Angular body (`error`) takes precedence over
 * NSwag's raw response text (`response`), so a raw fallback never overrides structured evidence.
 */
function readStructuredCode(
  error: object,
  known: readonly string[]
): string | null {
  return (
    readCode('error' in error ? error.error : null, known) ??
    readCode('response' in error ? error.response : null, known)
  );
}

function readCode(body: unknown, known: readonly string[]): string | null {
  if (typeof body === 'string') {
    try {
      // Cast back to `unknown`: JSON.parse returns `any`, which would otherwise
      // widen `body`'s type for the rest of this function and let the narrowing
      // checks below pass without the compiler actually enforcing the shape.
      body = JSON.parse(body) as unknown;
    } catch {
      return null;
    }
  }

  if (
    body &&
    typeof body === 'object' &&
    'code' in body &&
    typeof body.code === 'string' &&
    known.includes(body.code)
  ) {
    return body.code;
  }
  return null;
}
